// SettlementRecords.tsx
// Route: /admin/Settlementrecord
// Comprehensive Settlement & Vehicle Payout History with Advanced Filters & Excel Export

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FaSearch, FaSyncAlt, FaArrowLeft, FaArrowRight, FaChevronDown, FaChevronUp,
  FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimesCircle,
  FaFilter, FaMoneyBillWave, FaUser, FaTruck, FaBuilding,
  FaFileInvoiceDollar, FaCalendarAlt, FaFileExcel,
  FaUndo, FaRoute, FaBoxOpen, FaMoneyCheckAlt, FaLayerGroup,
  FaEdit, FaTrash,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type PaymentMode = "online" | "cash" | "bank" | "partial";

interface PaymentRecord {
  amount: number;
  paymentMode: PaymentMode;
  date: string;
}

export interface DynamicDeduction {
  id?: string;
  name: string;
  amount: string;
}

interface InvoiceUserPayment {
  userType: string;
  userName: string;
  amount: number;
  commission?: number;
  tds?: number;
  collection?: number;
  otherDeduction?: number;
  otherDeductionName?: string;
  otherDeductions?: Array<{ id?: string; name: string; amount: number }>;
  rate?: number;
  weight?: number;
  payableAmount?: number;
  paid: boolean;
  paymentRecords: PaymentRecord[];
}

interface InvoiceItem {
  id: number;
  product: string;
  hsn: string;
  uom: string;
  qty: number;
  rate: number;
  amount: number;
}

interface Invoice {
  id: string;
  lrNumber: string;
  invoiceNumber: string;
  company: string;
  companyAddress?: string;
  date: string;
  sender: string;
  receiver: string;
  broker: string;
  transporter: string;
  driver: string;
  route: string;
  origin: string;
  destination: string;
  vehicle: string;
  payment: number;
  items: InvoiceItem[];
  userDistributions: InvoiceUserPayment[];
  status: "paid" | "partial" | "generated";
}

interface EntityAllocation {
  invoice: Invoice;
  distribution: InvoiceUserPayment;
}

interface EntityGroup {
  key: string;
  userType: string;
  userName: string;
  invoiceCount: number;
  totalGross: number;
  totalCommission: number;
  totalTds: number;
  totalCollection: number;
  totalOtherDeduction: number;
  totalNetPayable: number;
  totalPaid: number;
  totalRemaining: number;
  allocations: EntityAllocation[];
}

type TabKey = "entities" | "trips";

const USER_TYPE_LABELS: Record<string, string> = {
  sender: "Sender (Consignor)", receiver: "Receiver (Consignee)", broker: "Broker",
  driver: "Driver", vehicle: "Vehicle", transporter: "Transporter", company: "Company",
  consignor: "Consignor", gst: "GST", tds: "TDS", profit: "Profit",
};

const MODE_COLORS: Record<PaymentMode, string> = {
  online:  "bg-blue-100 text-blue-800",
  cash:    "bg-green-100 text-green-800",
  bank:    "bg-purple-100 text-purple-800",
  partial: "bg-amber-100 text-amber-800",
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  vehicle:     <FaTruck className="text-orange-500" />,
  driver:      <FaTruck className="text-blue-500" />,
  receiver:    <FaBuilding className="text-purple-500" />,
  sender:      <FaBuilding className="text-indigo-500" />,
  transporter: <FaTruck className="text-green-500" />,
  broker:      <FaUser className="text-teal-500" />,
  company:     <FaBuilding className="text-amber-500" />,
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function safeToString(val: any): string {
  if (val == null) return "";
  if (typeof val === "object") {
    if (val.name) return String(val.name);
    if (val.companyName) return String(val.companyName);
    if (val.company_name) return String(val.company_name);
    if (val.title) return String(val.title);
    return "";
  }
  return String(val);
}

function fmt(n: number) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

function fmtDate(d: any): string {
  if (!d) return "—";
  const str = String(d);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, day] = match;
    const dateObj = new Date(Number(y), Number(m) - 1, Number(day));
    return dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }
  try {
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    }
  } catch {}
  return str;
}

function getNetPayable(u: InvoiceUserPayment): number {
  if (u.payableAmount != null && u.payableAmount > 0) return Number(u.payableAmount);
  const comm = Number(u.commission || 0);
  const tds = Number(u.tds || 0);
  const coll = Number(u.collection || 0);
  let oth = Number(u.otherDeduction || 0);
  if (Array.isArray(u.otherDeductions) && u.otherDeductions.length > 0) {
    oth = u.otherDeductions.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  }
  return Math.max(0, Number(u.amount || 0) - comm - tds - coll - oth);
}

function sumPaid(u: InvoiceUserPayment): number {
  return (u.paymentRecords || []).reduce((a, r) => a + Number(r.amount || 0), 0);
}

const companyCache = new Map<number, string>();
async function fetchCompanyName(id: number): Promise<string> {
  if (!id) return "—";
  if (companyCache.has(id)) return companyCache.get(id)!;
  try {
    const res = await fetch(`${API_BASE}/companyauth/company/${id}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const name = data.company_name || data.companyName || data.name || data.firm_name || `#${id}`;
    companyCache.set(id, name);
    return name;
  } catch {
    companyCache.set(id, `#${id}`);
    return `#${id}`;
  }
}

const userCache = new Map<number, { name: string; address: string }>();
async function fetchUsersBatch(ids: number[]): Promise<Map<number, { name: string; address: string }>> {
  const uncached = ids.filter((id) => !userCache.has(id));
  if (uncached.length > 0) {
    try {
      const res = await fetch(`${API_BASE}/auth/users/batch?ids=${uncached.join(",")}`, { credentials: "include" });
      if (res.ok) {
        const byId: Record<string, { name: string; address: string }> = await res.json();
        for (const idStr of Object.keys(byId)) {
          const id = Number(idStr);
          userCache.set(id, byId[idStr] || { name: `#${id}`, address: "" });
        }
      }
    } catch {}
    for (const id of uncached) {
      if (!userCache.has(id)) userCache.set(id, { name: `#${id}`, address: "" });
    }
  }
  const result = new Map<number, { name: string; address: string }>();
  ids.forEach((id) => result.set(id, userCache.get(id)!));
  return result;
}

async function fetchDriverNamesBatch(ids: number[]): Promise<Map<number, string>> {
  const result = new Map<number, string>();
  if (ids.length === 0) return result;
  try {
    const res = await fetch(`${API_BASE}/auth/drivers/batch?ids=${ids.join(",")}`, { credentials: "include" });
    const byId: Record<string, string> = res.ok ? await res.json() : {};
    ids.forEach((id) => result.set(id, byId[id] || `#${id}`));
  } catch {
    ids.forEach((id) => result.set(id, `#${id}`));
  }
  return result;
}

async function fetchVehicleNumbersBatch(ids: number[]): Promise<Map<number, string>> {
  const result = new Map<number, string>();
  if (ids.length === 0) return result;
  try {
    const res = await fetch(`${API_BASE}/auth/vehicles/batch?ids=${ids.join(",")}`, { credentials: "include" });
    const byId: Record<string, string> = res.ok ? await res.json() : {};
    ids.forEach((id) => result.set(id, byId[id] || `#${id}`));
  } catch {
    ids.forEach((id) => result.set(id, `#${id}`));
  }
  return result;
}

async function loadSettlementFromDB(invoiceId: string): Promise<InvoiceUserPayment[] | null> {
  try {
    const res = await fetch(`${API_BASE}/settlment/${invoiceId}`, { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !Array.isArray(data.distributions)) return null;

    return data.distributions.map((d: any) => {
      const grossAmt = Number(d.amount || 0);
      const comm = Number(d.commission || 0);
      const tds = Number(d.tds || 0);
      const coll = Number(d.collection || 0);

      let otherDeductionsList: Array<{ name: string; amount: number }> = [];
      const rawOthName = String(d.otherDeductionName || d.other_deduction_name || "");

      if (Array.isArray(d.otherDeductions)) {
        otherDeductionsList = (d.otherDeductions as any[]).map((od) => ({
          name: String(od.name || ""),
          amount: Number(od.amount || 0),
        }));
      } else if (rawOthName.startsWith("[")) {
        try {
          const parsed = JSON.parse(rawOthName);
          if (Array.isArray(parsed)) {
            otherDeductionsList = parsed.map((od: any) => ({
              name: String(od.name || ""),
              amount: Number(od.amount || 0),
            }));
          }
        } catch {}
      }

      let oth = Number(d.otherDeduction || d.other_deduction || 0);
      if (otherDeductionsList.length > 0) {
        oth = otherDeductionsList.reduce((s, x) => s + Number(x.amount || 0), 0);
      } else if (oth > 0) {
        otherDeductionsList = [{ name: rawOthName || "Other", amount: oth }];
      }

      const rateVal = Number(d.rate || 0);
      const weightVal = Number(d.weight || 0);
      const netPayable = d.net_amount != null
        ? Number(d.net_amount)
        : Math.max(0, grossAmt - comm - tds - coll - oth);

      return {
        userType: String(d.user_type || d.userType || ""),
        userName: String(d.user_name || d.userName || ""),
        amount: grossAmt,
        commission: comm,
        tds: tds,
        collection: coll,
        otherDeduction: oth,
        otherDeductionName: rawOthName,
        otherDeductions: otherDeductionsList,
        rate: rateVal,
        weight: weightVal,
        payableAmount: netPayable,
        paid: Boolean(d.paid),
        paymentRecords: Array.isArray(d.paymentRecords)
          ? (d.paymentRecords as Array<Record<string, unknown>>).map((r) => ({
              amount: Number(r.amount || 0),
              paymentMode: String(r.paymentMode || r.payment_mode || "bank") as PaymentMode,
              date: String(r.date || r.payment_date || ""),
            }))
          : [],
      };
    });
  } catch {
    return null;
  }
}

async function saveSettlementToDB(invoice: Invoice): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/settlment/${invoice.id}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        invoiceNo:     invoice.invoiceNumber,
        totalAmount:   invoice.userDistributions.reduce((sum, d) => sum + d.amount, 0),
        distributions: invoice.userDistributions.map((d) => {
          const othList = Array.isArray(d.otherDeductions) && d.otherDeductions.length > 0
            ? d.otherDeductions
            : (d.otherDeduction ? [{ name: d.otherDeductionName || "Other", amount: d.otherDeduction }] : []);
          const othTotal = othList.reduce((s, x) => s + Number(x.amount || 0), 0);
          const othSummary = d.otherDeductionName || (othList.length === 1 ? othList[0].name : othList.map((x) => x.name).join(", "));
          return {
            userType:           d.userType,
            userName:           d.userName,
            amount:             d.amount,
            commission:         d.commission || 0,
            tds:                d.tds || 0,
            collection:         d.collection || 0,
            otherDeduction:     othTotal,
            otherDeductionName: othSummary,
            otherDeductions:    othList,
            rate:               d.rate || 0,
            weight:             d.weight || 0,
            payableAmount:      getNetPayable(d),
            paymentRecords:     d.paymentRecords,
          };
        }),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function fetchAllInvoicesWithSettlements(): Promise<Invoice[]> {
  const [invRes, bulkSettlRes] = await Promise.all([
    fetch(`${API_BASE}/invoiceapi/invoice`, { credentials: "include" }),
    fetch(`${API_BASE}/settlment/all-bulk`, { credentials: "include" }).catch(() => null),
  ]);

  if (!invRes.ok) throw new Error(`HTTP ${invRes.status}: ${invRes.statusText}`);
  const rows: any[] = await invRes.json();
  if (!Array.isArray(rows)) throw new Error("Unexpected response format");

  const bulkMap: Record<string, InvoiceUserPayment[]> = bulkSettlRes && bulkSettlRes.ok ? await bulkSettlRes.json() : {};

  const userIds = new Set<number>();
  const driverIds = new Set<number>();
  const companyIds = new Set<number>();
  const vehicleIds = new Set<number>();

  for (const row of rows) {
    if (row.sender != null && !isNaN(Number(row.sender)) && Number(row.sender) > 0) userIds.add(Number(row.sender));
    if (row.receiver != null && !isNaN(Number(row.receiver)) && Number(row.receiver) > 0) userIds.add(Number(row.receiver));
    if (row.broker != null && !isNaN(Number(row.broker)) && Number(row.broker) > 0) userIds.add(Number(row.broker));
    if (row.transporter != null && !isNaN(Number(row.transporter)) && Number(row.transporter) > 0) userIds.add(Number(row.transporter));
    if (row.driver != null && !isNaN(Number(row.driver)) && Number(row.driver) > 0) driverIds.add(Number(row.driver));
    if (row.company != null && !isNaN(Number(row.company)) && Number(row.company) > 0) companyIds.add(Number(row.company));
    
    const rawVeh = row.vehicles ?? row.vehicle ?? row.vehicle_id ?? row.vehicles_id ?? row.vehicle_number ?? row.vehicleNo ?? row.vehicle_no;
    if (rawVeh != null && !isNaN(Number(rawVeh)) && Number(rawVeh) > 0) {
      vehicleIds.add(Number(rawVeh));
    }
  }

  const [usersMap, driverNamesMap, vehicleNumbersMap] = await Promise.all([
    fetchUsersBatch(Array.from(userIds)),
    fetchDriverNamesBatch(Array.from(driverIds)),
    fetchVehicleNumbersBatch(Array.from(vehicleIds)),
  ]);

  const companyNamesMap = new Map<number, string>();
  await Promise.all(
    Array.from(companyIds).map(async (cid) => {
      const name = await fetchCompanyName(cid);
      companyNamesMap.set(cid, name);
    })
  );

  const mapped: Invoice[] = rows.map((row) => {
    const companyName = row.company && companyNamesMap
      ? (companyNamesMap.get(row.company) ?? `#${row.company}`)
      : "—";
    const sender = usersMap.get(row.sender ?? -1);
    const receiver = usersMap.get(row.receiver ?? -1);
    const broker = usersMap.get(row.broker ?? -1);
    const transporter = usersMap.get(row.transporter ?? -1);
    const driverName = row.driver && driverNamesMap
      ? (driverNamesMap.get(row.driver) ?? `#${row.driver}`)
      : "—";

    const rawVeh = row.vehicles ?? row.vehicle ?? row.vehicle_id ?? row.vehicles_id ?? row.vehicle_number ?? row.vehicleNo ?? row.vehicle_no;
    let vehicleNo = "—";
    if (rawVeh != null && String(rawVeh).trim()) {
      const num = Number(rawVeh);
      if (!isNaN(num) && num > 0 && vehicleNumbersMap.has(num)) {
        vehicleNo = vehicleNumbersMap.get(num) || `#${num}`;
      } else if (typeof rawVeh === "string" && isNaN(Number(rawVeh))) {
        vehicleNo = rawVeh.trim();
      } else if (!isNaN(num) && num > 0) {
        vehicleNo = vehicleNumbersMap.get(num) ?? `#${num}`;
      }
    }

    const items: InvoiceItem[] = Array.isArray(row.items)
      ? row.items.map((it: any) => ({
          id: Number(it.id || 0),
          product: safeToString(it.product || it.items || it.name || "Item"),
          hsn: safeToString(it.hsn || ""),
          uom: safeToString(it.uom || "—"),
          qty: Number(it.qty || it.weight || 0),
          rate: Number(it.rate || 0),
          amount: Number(it.amount || 0),
        }))
      : [];

    const rawInvNo = safeToString(row["InvoiceNo."] || row.InvoiceNo || row.invoiceno || row.invoiceNo || row.invoice_no || `INV-${row.id}`);
    const rawLrNo  = safeToString(row.lr_no || row.lrNumber || row.lr_number || row.biltyNo || rawInvNo);

    const inv: Invoice = {
      id: String(row.id),
      lrNumber: rawLrNo,
      invoiceNumber: rawInvNo,
      company: companyName,
      date: row.date || row.created_at || "",
      sender: sender ? sender.name : "—",
      receiver: receiver ? receiver.name : "—",
      broker: broker ? broker.name : "—",
      transporter: transporter ? transporter.name : "—",
      driver: driverName,
      vehicle: vehicleNo,
      route: row.source && row.destination ? `${row.source} → ${row.destination}` : "—",
      origin: row.source || "—",
      destination: row.destination || "—",
      payment: Number(row.payment || 0),
      items,
      userDistributions: [],
      status: (row.paymentStatus || "").toLowerCase() === "paid" ? "paid" : "generated",
    };

    if (bulkMap[inv.id] && bulkMap[inv.id].length > 0) {
      inv.userDistributions = bulkMap[inv.id];
      const allPaid = inv.userDistributions.every((d) => d.paid);
      const anyPaid = inv.userDistributions.some((d) => sumPaid(d) > 0);
      inv.status = allPaid ? "paid" : anyPaid ? "partial" : "generated";
    }

    return inv;
  });

  return mapped;
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const SettlementRecords: React.FC = () => {
  const navigate = useNavigate();
  const { entityKey } = useParams<{ entityKey?: string }>();
  const selectedEntityKey = entityKey ? decodeURIComponent(entityKey) : null;

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const [tab, setTab]           = useState<TabKey>("entities");

  const [payModal, setPayModal] = useState<{
    invoiceId: string; userIdx: number; amt: string; mode: PaymentMode; date: string;
    full?: boolean; editing?: boolean; editingIdx?: number;
  } | null>(null);

  const [entityPayModal, setEntityPayModal] = useState<{
    grpKey: string;
    userName: string;
    userType: string;
    totalRemaining: number;
    amt: string;
    mode: PaymentMode;
    date: string;
    full?: boolean;
  } | null>(null);

  const [editDist, setEditDist] = useState<{
    invoiceId: string;
    editingIdx: number | null;
    source: "auto" | "manual";
    selectedParticipant: string;
    userType: string;
    userName: string;
    amount: string;
    commission: string;
    tds: string;
    collection: string;
    otherDeductions: DynamicDeduction[];
    allocMode?: "direct" | "rate_weight";
    itemRate?: string;
    itemWeight?: string;
  } | null>(null);

  function getTodayLocalDate(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function formatYMD(d: any): string {
    if (!d) return getTodayLocalDate();
    const str = String(d);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    try {
      const dt = new Date(d);
      if (!isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const day = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      }
    } catch {}
    return str.slice(0, 10);
  }

  function openPay(invoiceId: string, userIdx: number, amt: number, mode: PaymentMode, date: string, full = false, editing = false, editingIdx?: number) {
    setPayModal({ invoiceId, userIdx, amt: amt.toString(), mode, date, full, editing, editingIdx });
  }

  function handlePayment(invoiceId: string, userIdx: number, amt: number, mode: PaymentMode, date: string, editingIdx?: number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const dists = inv.userDistributions.map((u, i) => {
        if (i !== userIdx) return u;
        const netPay = getNetPayable(u);
        const otherPaid = u.paymentRecords.reduce((sum, r, idx) =>
          sum + (editingIdx === idx ? 0 : r.amount), 0
        );
        const maxAllowed = Math.max(0, netPay - otherPaid);
        const safeAmt = Math.min(amt, maxAllowed);

        let recs = [...u.paymentRecords];
        if (typeof editingIdx === "number") recs[editingIdx] = { amount: safeAmt, paymentMode: mode, date };
        else recs.push({ amount: safeAmt, paymentMode: mode, date });
        recs = recs.filter((r) => r.amount > 0);
        return { ...u, paymentRecords: recs, paid: recs.reduce((a, r) => a + r.amount, 0) >= netPay };
      });
      const status = dists.every((d) => d.paid) ? "paid" : dists.some((d) => sumPaid(d) > 0) ? "partial" : "generated";
      updatedInvoice = { ...inv, userDistributions: dists, status };
      return updatedInvoice;
    }));
    setPayModal(null);
    setTimeout(() => {
      if (updatedInvoice) saveSettlementToDB(updatedInvoice);
    }, 100);
  }

  function openEntityPay(grpKey: string, userName: string, userType: string, totalRemaining: number, full = false) {
    setEntityPayModal({
      grpKey,
      userName,
      userType,
      totalRemaining,
      amt: totalRemaining.toString(),
      mode: "bank",
      date: getTodayLocalDate(),
      full,
    });
  }

  function handleEntityPayment(grpKey: string, amtToPay: number, mode: PaymentMode, date: string) {
    const grp = entityGroups.find((g) => g.key === grpKey);
    if (!grp || amtToPay <= 0) return;

    let remainingToDistribute = amtToPay;
    const updatedInvoicesMap = new Map<string, Invoice>();

    for (const { invoice: inv, distribution: dist } of grp.allocations) {
      if (remainingToDistribute <= 0) break;

      const net = getNetPayable(dist);
      const paid = sumPaid(dist);
      const tripRem = Math.max(0, net - paid);
      if (tripRem <= 0) continue;

      const payForThisTrip = Math.min(remainingToDistribute, tripRem);
      remainingToDistribute -= payForThisTrip;

      const targetInv = updatedInvoicesMap.get(inv.id) || invoices.find((i) => i.id === inv.id) || inv;
      const distIdx = targetInv.userDistributions.findIndex(
        (d) => d.userType === dist.userType && d.userName === dist.userName
      );
      if (distIdx === -1) continue;

      const newDists = targetInv.userDistributions.map((u, i) => {
        if (i !== distIdx) return u;
        const recs = [...u.paymentRecords, { amount: payForThisTrip, paymentMode: mode, date }];
        const netP = getNetPayable(u);
        const isPaid = recs.reduce((a, r) => a + r.amount, 0) >= netP;
        return { ...u, paymentRecords: recs, paid: isPaid };
      });

      const allPaid = newDists.every((d) => d.paid);
      const anyPaid = newDists.some((d) => sumPaid(d) > 0);
      const newStatus: "paid" | "partial" | "generated" = allPaid ? "paid" : anyPaid ? "partial" : "generated";

      const updatedInv: Invoice = { ...targetInv, userDistributions: newDists, status: newStatus };
      updatedInvoicesMap.set(targetInv.id, updatedInv);
    }

    if (updatedInvoicesMap.size > 0) {
      setInvoices((prev) =>
        prev.map((inv) => updatedInvoicesMap.get(inv.id) || inv)
      );
      setTimeout(() => {
        updatedInvoicesMap.forEach((inv) => saveSettlementToDB(inv));
      }, 150);
    }

    setEntityPayModal(null);
  }

  function removePayRec(invoiceId: string, userIdx: number, payIdx: number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const dists = inv.userDistributions.map((u, i) => {
        if (i !== userIdx) return u;
        const recs = u.paymentRecords.filter((_, j) => j !== payIdx);
        return { ...u, paymentRecords: recs, paid: recs.reduce((a, r) => a + r.amount, 0) >= getNetPayable(u) };
      });
      const status = dists.every((d) => d.paid) ? "paid" : dists.some((d) => sumPaid(d) > 0) ? "partial" : "generated";
      updatedInvoice = { ...inv, userDistributions: dists, status };
      return updatedInvoice;
    }));
    setTimeout(() => {
      if (updatedInvoice) saveSettlementToDB(updatedInvoice);
    }, 100);
  }

  function openEditDist(invoice: Invoice, idx: number | null, u?: InvoiceUserPayment) {
    const sumAllocated = (dists: InvoiceUserPayment[]) => dists.reduce((a, d) => a + (d.amount || 0), 0);
    const remaining = Math.max(0, invoice.payment - sumAllocated(invoice.userDistributions));
    const totalItemsWeight = invoice.items && invoice.items.length > 0
      ? invoice.items.reduce((s, it) => s + Number(it.qty || 0), 0)
      : 0;
    const defaultWeight = totalItemsWeight > 0
      ? totalItemsWeight.toString()
      : invoice.items && invoice.items[0]?.qty ? invoice.items[0].qty.toString() : "";
    const defaultRate = invoice.items && invoice.items.length > 0 && invoice.items[0]?.rate ? invoice.items[0].rate.toString() : "";

    if (u) {
      const existingOth: DynamicDeduction[] = Array.isArray(u.otherDeductions) && u.otherDeductions.length > 0
        ? u.otherDeductions.map((od, i) => ({ id: String(i + 1), name: od.name, amount: od.amount.toString() }))
        : (u.otherDeduction ? [{ id: "1", name: u.otherDeductionName || "Other", amount: u.otherDeduction.toString() }] : []);

      setEditDist({
        invoiceId: invoice.id, editingIdx: idx, source: "manual",
        selectedParticipant: "",
        userType: u.userType, userName: u.userName, amount: u.amount?.toString() || "",
        commission: u.commission ? u.commission.toString() : "",
        tds: u.tds ? u.tds.toString() : "",
        collection: u.collection ? u.collection.toString() : "",
        otherDeductions: existingOth,
        allocMode: u.rate || u.weight ? "rate_weight" : "direct",
        itemWeight: u.weight ? u.weight.toString() : defaultWeight,
        itemRate: u.rate ? u.rate.toString() : defaultRate,
      });
    } else {
      setEditDist({
        invoiceId: invoice.id, editingIdx: null, source: "manual",
        selectedParticipant: "",
        userType: "vehicle", userName: invoice.vehicle && invoice.vehicle !== "—" ? invoice.vehicle : "",
        amount: remaining > 0 ? remaining.toString() : "",
        commission: "", tds: "", collection: "",
        otherDeductions: [],
        allocMode: "direct", itemWeight: defaultWeight, itemRate: defaultRate,
      });
    }
  }

  function saveEditDist() {
    if (!editDist) return;
    const { invoiceId, editingIdx, userType, userName, amount, commission, tds, collection, otherDeductions, itemRate, itemWeight } = editDist;
    const grossAmt = Number(amount || 0);
    if (!userType || grossAmt < 1) return;

    const commVal = Number(commission || 0);
    const tdsVal = Number(tds || 0);
    const collVal = Number(collection || 0);

    const validOtherDeductions = (otherDeductions || [])
      .filter((od) => Number(od.amount || 0) > 0 || od.name.trim().length > 0)
      .map((od) => ({
        name: od.name.trim() || "Other",
        amount: Number(od.amount || 0),
      }));

    const totalOtherDeduction = validOtherDeductions.reduce((s, x) => s + x.amount, 0);
    const othSummaryName = validOtherDeductions.length === 1
      ? validOtherDeductions[0].name
      : validOtherDeductions.map((x) => x.name).join(", ");

    const totalDed = commVal + tdsVal + collVal + totalOtherDeduction;
    if (totalDed > grossAmt) return;
    const netPayable = Math.max(0, grossAmt - totalDed);

    const rateVal = Number(itemRate || 0);
    const weightVal = Number(itemWeight || 0);

    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const sumAllocated = (dists: InvoiceUserPayment[]) => dists.reduce((a, d) => a + (d.amount || 0), 0);
      const others = inv.userDistributions.filter((_, i) => i !== editingIdx);
      if (sumAllocated(others) + grossAmt > inv.payment) return inv;
      let list = [...inv.userDistributions];
      const existingRecs = editingIdx != null ? inv.userDistributions[editingIdx].paymentRecords : [];
      const existingPaid = existingRecs.reduce((a, r) => a + r.amount, 0);
      const entry: InvoiceUserPayment = {
        userType, userName, amount: grossAmt,
        commission: commVal, tds: tdsVal, collection: collVal,
        otherDeduction: totalOtherDeduction,
        otherDeductionName: othSummaryName,
        otherDeductions: validOtherDeductions,
        rate: rateVal,
        weight: weightVal,
        payableAmount: netPayable,
        paid: existingPaid >= netPayable && netPayable > 0,
        paymentRecords: existingRecs,
      };
      if (editingIdx != null) list[editingIdx] = entry;
      else list.push(entry);
      const status = list.every((d) => d.paid) ? "paid" : list.some((d) => sumPaid(d) > 0) ? "partial" : "generated";
      updatedInvoice = { ...inv, userDistributions: list, status };
      return updatedInvoice;
    }));
    setEditDist(null);
    setTimeout(() => {
      if (updatedInvoice) saveSettlementToDB(updatedInvoice);
    }, 100);
  }

  // Advanced Filters State
  const [showFilters, setShowFilters]                 = useState(false);
  const [search, setSearch]                           = useState("");
  const [filterCompany, setFilterCompany]             = useState("");
  const [filterParty, setFilterParty]                 = useState("");
  const [filterVehicle, setFilterVehicle]             = useState("");
  const [filterUserType, setFilterUserType]           = useState("");
  const [filterStatus, setFilterStatus]               = useState("");
  const [filterDeductionType, setFilterDeductionType] = useState("");
  const [filterDateFrom, setFilterDateFrom]           = useState("");
  const [filterDateTo, setFilterDateTo]               = useState("");

  const activeFilterCount = [
    filterCompany, filterParty, filterVehicle, filterUserType,
    filterStatus, filterDeductionType, filterDateFrom, filterDateTo,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearch("");
    setFilterCompany("");
    setFilterParty("");
    setFilterVehicle("");
    setFilterUserType("");
    setFilterStatus("");
    setFilterDeductionType("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchAllInvoicesWithSettlements();
      setInvoices(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load settlement records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Unique Dropdown Options
  const uniqueCompanies = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      const c = safeToString(inv.company);
      if (c && c !== "—") set.add(c);
    });
    return Array.from(set).sort();
  }, [invoices]);

  const uniqueParties = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      [inv.sender, inv.receiver, inv.transporter, inv.driver, inv.broker].forEach((p) => {
        const str = safeToString(p);
        if (str && str !== "—") set.add(str);
      });
      inv.userDistributions.forEach((d) => {
        const u = safeToString(d.userName);
        if (u && u !== "—") set.add(u);
      });
    });
    return Array.from(set).sort();
  }, [invoices]);

  const uniqueVehicles = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      const v = safeToString(inv.vehicle);
      if (v && v !== "—") set.add(v);
    });
    return Array.from(set).sort();
  }, [invoices]);

  // Filtered Invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const searchStr = `${safeToString(inv.lrNumber)} ${safeToString(inv.invoiceNumber)} ${safeToString(inv.company)} ${safeToString(inv.origin)} ${safeToString(inv.destination)} ${safeToString(inv.sender)} ${safeToString(inv.receiver)} ${safeToString(inv.driver)} ${safeToString(inv.vehicle)} ${safeToString(inv.broker)}`.toLowerCase();
      if (search && !searchStr.includes(search.toLowerCase())) return false;

      if (filterCompany && safeToString(inv.company).toLowerCase() !== filterCompany.toLowerCase()) return false;
      if (filterVehicle && safeToString(inv.vehicle).toLowerCase() !== filterVehicle.toLowerCase()) return false;

      if (filterParty) {
        const pTarget = filterParty.toLowerCase();
        const match =
          safeToString(inv.sender).toLowerCase() === pTarget ||
          safeToString(inv.receiver).toLowerCase() === pTarget ||
          safeToString(inv.transporter).toLowerCase() === pTarget ||
          safeToString(inv.driver).toLowerCase() === pTarget ||
          safeToString(inv.broker).toLowerCase() === pTarget ||
          inv.userDistributions.some((d) => safeToString(d.userName).toLowerCase() === pTarget);
        if (!match) return false;
      }

      if (filterStatus) {
        if (inv.status !== filterStatus) return false;
      }

      if (filterUserType) {
        const hasType = inv.userDistributions.some((d) => d.userType === filterUserType) ||
          (filterUserType === "driver" && inv.driver && inv.driver !== "—") ||
          (filterUserType === "vehicle" && inv.vehicle && inv.vehicle !== "—") ||
          (filterUserType === "broker" && inv.broker && inv.broker !== "—") ||
          (filterUserType === "receiver" && inv.receiver && inv.receiver !== "—") ||
          (filterUserType === "sender" && inv.sender && inv.sender !== "—");
        if (!hasType) return false;
      }

      if (filterDateFrom) {
        const d = inv.date ? inv.date.slice(0, 10) : "";
        if (!d || d < filterDateFrom) return false;
      }
      if (filterDateTo) {
        const d = inv.date ? inv.date.slice(0, 10) : "";
        if (!d || d > filterDateTo) return false;
      }

      if (filterDeductionType) {
        if (filterDeductionType === "any") {
          const hasD = inv.userDistributions.some((d) =>
            ((d.commission || 0) + (d.tds || 0) + (d.collection || 0) + (d.otherDeduction || 0)) > 0
          );
          if (!hasD) return false;
        } else if (filterDeductionType === "commission") {
          if (!inv.userDistributions.some((d) => (d.commission || 0) > 0)) return false;
        } else if (filterDeductionType === "tds") {
          if (!inv.userDistributions.some((d) => (d.tds || 0) > 0)) return false;
        } else if (filterDeductionType === "collection") {
          if (!inv.userDistributions.some((d) => (d.collection || 0) > 0)) return false;
        } else if (filterDeductionType === "other") {
          if (!inv.userDistributions.some((d) => (d.otherDeduction || 0) > 0)) return false;
        }
      }

      return true;
    });
  }, [invoices, search, filterCompany, filterParty, filterVehicle, filterUserType, filterStatus, filterDeductionType, filterDateFrom, filterDateTo]);

  // Group Allocations by Entity (Vehicle / Person)
  const entityGroups = useMemo(() => {
    const map = new Map<string, EntityGroup>();

    filteredInvoices.forEach((inv) => {
      inv.userDistributions.forEach((dist) => {
        const name = safeToString(dist.userName);
        if (!name || name === "—") return;
        const key = `${dist.userType}::${name}`;

        const comm = Number(dist.commission || 0);
        const tds  = Number(dist.tds || 0);
        const coll = Number(dist.collection || 0);
        const othList = Array.isArray(dist.otherDeductions) && dist.otherDeductions.length > 0
          ? dist.otherDeductions
          : (dist.otherDeduction ? [{ name: dist.otherDeductionName || "Other", amount: dist.otherDeduction }] : []);
        const oth  = othList.reduce((s, x) => s + Number(x.amount || 0), 0) || Number(dist.otherDeduction || 0);
        const gross = Number(dist.amount || 0);
        const net  = getNetPayable(dist);
        const paid = sumPaid(dist);
        const rem  = Math.max(0, net - paid);

        if (!map.has(key)) {
          map.set(key, {
            key,
            userType: dist.userType,
            userName: name,
            invoiceCount: 0,
            totalGross: 0,
            totalCommission: 0,
            totalTds: 0,
            totalCollection: 0,
            totalOtherDeduction: 0,
            totalNetPayable: 0,
            totalPaid: 0,
            totalRemaining: 0,
            allocations: [],
          });
        }

        const grp = map.get(key)!;
        grp.invoiceCount += 1;
        grp.totalGross += gross;
        grp.totalCommission += comm;
        grp.totalTds += tds;
        grp.totalCollection += coll;
        grp.totalOtherDeduction += oth;
        grp.totalNetPayable += net;
        grp.totalPaid += paid;
        grp.totalRemaining += rem;
        grp.allocations.push({ invoice: inv, distribution: dist });
      });
    });

    return Array.from(map.values()).sort((a, b) => b.totalNetPayable - a.totalNetPayable);
  }, [filteredInvoices]);

  // Total Summary KPI Metrics
  const summaryMetrics = useMemo(() => {
    let gross = 0, comm = 0, tds = 0, coll = 0, oth = 0, net = 0, paid = 0, rem = 0;
    entityGroups.forEach((g) => {
      gross += g.totalGross;
      comm  += g.totalCommission;
      tds   += g.totalTds;
      coll  += g.totalCollection;
      oth   += g.totalOtherDeduction;
      net   += g.totalNetPayable;
      paid  += g.totalPaid;
      rem   += g.totalRemaining;
    });
    return { gross, comm, tds, coll, oth, net, paid, rem, entityCount: entityGroups.length };
  }, [entityGroups]);

  // Export to Excel / CSV Function (supports all or single vehicle target, sorted ASC by date)
  const exportToExcel = (targetGroups?: EntityGroup[], customFilename?: string) => {
    const groupsToExport = targetGroups || entityGroups;
    if (groupsToExport.length === 0) return;

    const headers = [
      "Company Name (Hired By)",
      "Vehicle / Entity Name",
      "User Type",
      "Vehicle Number",
      "LR Number",
      "Trip Date",
      "Origin",
      "Destination",
      "Sender Name",
      "Receiver Name",
      "Product / Items",
      "Invoice Weight / Qty",
      "Invoice Rate (₹)",
      "Invoice Amount (₹)",
      "Allocated Weight / Qty",
      "Allocated Rate (₹)",
      "Gross Allocated (₹)",
      "Commission (₹)",
      "TDS (₹)",
      "Collection (₹)",
      "Other Deductions Breakdown",
      "Other Deductions Total (₹)",
      "Total Deductions (₹)",
      "Net Payable (₹)",
      "Total Paid (₹)",
      "Remaining Due (₹)",
      "Payout Status",
      "Payment History Logs",
    ];

    const allAllocations: Array<{
      invoice: Invoice;
      dist: InvoiceUserPayment;
      grp: EntityGroup;
    }> = [];

    groupsToExport.forEach((grp) => {
      grp.allocations.forEach(({ invoice, distribution: dist }) => {
        allAllocations.push({ invoice, dist, grp });
      });
    });

    const parseDateToTime = (d: any): number => {
      if (!d) return 0;
      const str = String(d);
      const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
      }
      try {
        const dt = new Date(d);
        if (!isNaN(dt.getTime())) return dt.getTime();
      } catch {}
      return 0;
    };

    // Sort in ASCENDING order of trip date (e.g. 1 Aug, 2 Aug, 3 Aug...)
    allAllocations.sort((a, b) => {
      const timeA = parseDateToTime(a.invoice.date);
      const timeB = parseDateToTime(b.invoice.date);
      if (timeA !== timeB) return timeA - timeB;
      return safeToString(a.invoice.lrNumber).localeCompare(safeToString(b.invoice.lrNumber));
    });

    const rows: string[][] = [];

    allAllocations.forEach(({ invoice, dist }) => {
      const rawComp = safeToString(invoice.company) || "—";
      const compWords = rawComp.replace(/[^a-zA-Z0-9\s]/g, " ").trim().split(/\s+/).filter(Boolean);
      const comp = rawComp === "—"
        ? "—"
        : compWords.length > 1
        ? compWords.map((w) => w[0].toUpperCase()).join("")
        : rawComp.toUpperCase();

      const entity  = safeToString(dist.userName) || "—";
      const uType   = USER_TYPE_LABELS[dist.userType] || dist.userType;
      const vehNo   = safeToString(invoice.vehicle) || (dist.userType === "vehicle" ? safeToString(dist.userName) : "—");
      const lrNo    = safeToString(invoice.lrNumber) || "—";
      const tDate   = invoice.date ? invoice.date.slice(0, 10) : "—";
      const orig    = safeToString(invoice.origin) || "—";
      const dest    = safeToString(invoice.destination) || "—";
      const sender  = safeToString(invoice.sender) || "—";
      const recv    = safeToString(invoice.receiver) || "—";

      const prodStr = invoice.items && invoice.items.length > 0
        ? invoice.items.map((it) => safeToString(it.product)).join(" | ")
        : "—";
      const weightStr = invoice.items && invoice.items.length > 0
        ? invoice.items.map((it) => `${it.qty || 0} ${it.uom !== "—" ? it.uom : ""}`).join(" | ")
        : "—";
      const rateStr = invoice.items && invoice.items.length > 0
        ? invoice.items.map((it) => `${it.rate || 0}`).join(" | ")
        : "—";
      const amountStr = invoice.items && invoice.items.length > 0
        ? invoice.items.map((it) => `${it.amount || 0}`).join(" | ")
        : "—";

      // Show allocated rate and weight when added, but if added by amount then don't show rate and weight
      const allocRate = dist.rate != null && Number(dist.rate) > 0
        ? dist.rate.toString()
        : "";

      const allocWeight = dist.weight != null && Number(dist.weight) > 0
        ? dist.weight.toString()
        : "";

      const gross   = Number(dist.amount || 0);
      const comm    = Number(dist.commission || 0);
      const tdsVal  = Number(dist.tds || 0);
      const collVal = Number(dist.collection || 0);

      const othList = Array.isArray(dist.otherDeductions) && dist.otherDeductions.length > 0
        ? dist.otherDeductions
        : (dist.otherDeduction ? [{ name: dist.otherDeductionName || "Other", amount: dist.otherDeduction }] : []);
      const othBreakdown = othList.map((x) => `${x.name}: ₹${Number(x.amount || 0).toLocaleString("en-IN")}`).join(" | ") || (dist.otherDeductionName || "None");
      const othVal  = othList.reduce((s, x) => s + Number(x.amount || 0), 0) || Number(dist.otherDeduction || 0);

      const totDed  = comm + tdsVal + collVal + othVal;
      const net     = getNetPayable(dist);
      const pAmt    = sumPaid(dist);
      const remAmt  = Math.max(0, net - pAmt);
      const status  = pAmt >= net && net > 0 ? "Fully Paid" : pAmt > 0 ? "Partially Paid" : "Unpaid";

      const pHistory = (dist.paymentRecords || [])
        .map((r) => `${r.date ? r.date.slice(0, 10) : ""}: ₹${r.amount} (${r.paymentMode})`)
        .join(" ; ");

      rows.push([
        comp, entity, uType, vehNo, lrNo, tDate, orig, dest, sender, recv,
        prodStr, weightStr, rateStr, amountStr,
        allocWeight, allocRate,
        gross.toString(), comm.toString(), tdsVal.toString(), collVal.toString(),
        othBreakdown, othVal.toString(), totDed.toString(), net.toString(),
        pAmt.toString(), remAmt.toString(), status, pHistory,
      ]);
    });

    const escapeCSV = (val: string) => {
      if (val.includes('"') || val.includes(",") || val.includes("\n")) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const csvContent = "\uFEFF" + [
      headers.map(escapeCSV).join(","),
      ...rows.map((r) => r.map(escapeCSV).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileName = customFilename || (targetGroups && targetGroups.length === 1
      ? `Settlement_Report_${targetGroups[0].userName.replace(/[^a-zA-Z0-9_-]/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`
      : `Settlement_Vehicle_Full_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const selectedGroup = useMemo(() => {
    return selectedEntityKey ? entityGroups.find((g) => g.key === selectedEntityKey) || null : null;
  }, [selectedEntityKey, entityGroups]);

  const selectedGroupMetrics = useMemo(() => {
    if (!selectedGroup) return { pctPaid: 100, totalDeductions: 0, isFullyPaid: false };
    const pctPaid = selectedGroup.totalNetPayable > 0
      ? Math.round((selectedGroup.totalPaid / selectedGroup.totalNetPayable) * 100)
      : 100;
    const totalDeductions = selectedGroup.totalCommission + selectedGroup.totalTds + selectedGroup.totalCollection + selectedGroup.totalOtherDeduction;
    const isFullyPaid = selectedGroup.totalRemaining === 0 && selectedGroup.totalNetPayable > 0;
    return { pctPaid, totalDeductions, isFullyPaid };
  }, [selectedGroup]);

  return (
    <div className="min-h-screen bg-slate-100 p-4 sm:p-6 text-slate-900">
      <div className="max-w-none w-full mx-auto space-y-6">

        {/* TOP HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/settlement")}
              className="p-2.5 rounded-xl bg-slate-100 border border-slate-300 text-slate-700 hover:bg-blue-600 hover:text-white transition shadow-2xs"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                Settlement & Vehicle Trip Records
              </h1>
              <p className="text-slate-500 font-medium text-xs sm:text-sm mt-0.5">
                Complete audit trail of vehicle allocations, company hires, route breakdowns, items, deductions & payments
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => exportToExcel()}
              disabled={entityGroups.length === 0}
              className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold text-xs sm:text-sm hover:bg-black transition shadow-xs disabled:opacity-50 flex items-center gap-2"
            >
              <FaFileExcel className="text-lg" /> Export Excel CSV
            </button>
            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`px-4 py-2 rounded-xl font-bold text-xs sm:text-sm transition flex items-center gap-2 border shadow-xs ${
                showFilters || activeFilterCount > 0
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              <FaFilter /> Advance Filters
              {activeFilterCount > 0 && (
                <span className="bg-white text-blue-800 rounded-full text-[11px] px-2 py-0.5 font-black">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white font-bold text-xs sm:text-sm hover:bg-black transition shadow-xs disabled:opacity-60 flex items-center gap-2"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />} Refresh
            </button>
          </div>
        </div>

        {/* SEARCH & ADVANCED FILTERS BAR */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 space-y-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="relative flex-1 min-w-[260px]">
              <FaSearch className="absolute top-1/2 left-3.5 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search Vehicle No / Driver / Company / LR# / Route / Item…"
                className="pl-10 pr-4 py-2 rounded-xl border border-slate-300 text-xs sm:text-sm text-slate-800 font-semibold w-full bg-slate-50 focus:bg-white focus:border-blue-500 focus:outline-none"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {(activeFilterCount > 0 || search) && (
              <button
                onClick={resetFilters}
                className="px-3.5 py-2 bg-slate-100 hover:bg-red-50 text-red-600 border border-slate-300 rounded-xl font-bold text-xs sm:text-sm transition flex items-center gap-1.5"
              >
                <FaUndo /> Reset All Filters
              </button>
            )}
          </div>

          {/* Expandable Advance Filter Dropdowns */}
          {showFilters && (
            <div className="pt-3 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Company (Hired By)</label>
                <select
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Companies</option>
                  {uniqueCompanies.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Party / Person</label>
                <select
                  value={filterParty}
                  onChange={(e) => setFilterParty(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Parties</option>
                  {uniqueParties.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Vehicle No.</label>
                <select
                  value={filterVehicle}
                  onChange={(e) => setFilterVehicle(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Vehicles</option>
                  {uniqueVehicles.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">User Type</label>
                <select
                  value={filterUserType}
                  onChange={(e) => setFilterUserType(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none capitalize w-full"
                >
                  <option value="">All Types</option>
                  <option value="vehicle">Vehicle</option>
                  <option value="driver">Driver</option>
                  <option value="broker">Broker</option>
                  <option value="receiver">Receiver</option>
                  <option value="sender">Sender</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Payout Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Statuses</option>
                  <option value="paid">Fully Paid</option>
                  <option value="partial">Partially Paid</option>
                  <option value="generated">Unsettled (Pending)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Deductions</label>
                <select
                  value={filterDeductionType}
                  onChange={(e) => setFilterDeductionType(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Invoices</option>
                  <option value="any">Has Any Deductions</option>
                  <option value="commission">Commission &gt; ₹0</option>
                  <option value="tds">TDS &gt; ₹0</option>
                  <option value="collection">Collection &gt; ₹0</option>
                  <option value="other">Other Deduction &gt; ₹0</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Date Range</label>
                <div className="flex gap-1 items-center">
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="rounded-lg border border-slate-300 px-1.5 py-1 font-semibold text-[11px] text-slate-800 bg-slate-50 w-full"
                  />
                  <span className="text-slate-400 font-bold">→</span>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="rounded-lg border border-slate-300 px-1.5 py-1 font-semibold text-[11px] text-slate-800 bg-slate-50 w-full"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SUMMARY KPI METRICS CARDS */}
        {entityGroups.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-xs sm:text-sm">
            <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xs">
              <span className="text-[11px] text-slate-500 font-bold uppercase block">Entities / Vehicles</span>
              <span className="font-black text-slate-900 text-base sm:text-lg mt-0.5 block">{summaryMetrics.entityCount}</span>
            </div>
            <div className="bg-blue-50/90 p-3.5 rounded-2xl border border-blue-100 shadow-2xs">
              <span className="text-[11px] text-blue-700 font-bold uppercase block">Gross Allocated</span>
              <span className="font-extrabold text-blue-950 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.gross)}</span>
            </div>
            <div className="bg-red-50/90 p-3.5 rounded-2xl border border-red-100 shadow-2xs">
              <span className="text-[11px] text-red-600 font-bold uppercase block">Total Deductions</span>
              <span className="font-extrabold text-red-700 text-base sm:text-lg mt-0.5 block">
                -{fmt(summaryMetrics.comm + summaryMetrics.tds + summaryMetrics.coll + summaryMetrics.oth)}
              </span>
            </div>
            <div className="bg-indigo-50/90 p-3.5 rounded-2xl border border-indigo-100 shadow-2xs">
              <span className="text-[11px] text-indigo-700 font-bold uppercase block">Net Payable</span>
              <span className="font-black text-indigo-950 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.net)}</span>
            </div>
            <div className="bg-green-50/90 p-3.5 rounded-2xl border border-green-100 shadow-2xs">
              <span className="text-[11px] text-green-700 font-bold uppercase block">Total Paid</span>
              <span className="font-extrabold text-green-800 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.paid)}</span>
            </div>
            <div className="bg-amber-50/90 p-3.5 rounded-2xl border border-amber-100 shadow-2xs">
              <span className="text-[11px] text-amber-700 font-bold uppercase block">Remaining Due</span>
              <span className="font-extrabold text-amber-800 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.rem)}</span>
            </div>
            <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200 shadow-2xs col-span-2 sm:col-span-1">
              <span className="text-[11px] text-slate-600 font-bold uppercase block">Payout Rate</span>
              <span className="font-black text-slate-900 text-base sm:text-lg mt-0.5 block">
                {summaryMetrics.net > 0 ? `${Math.round((summaryMetrics.paid / summaryMetrics.net) * 100)}%` : "100%"}
              </span>
            </div>
          </div>
        )}

        {/* LOADING & ERROR STATES */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 shadow-xs">
            <FaSpinner className="text-blue-600 text-4xl animate-spin mb-3" />
            <span className="text-slate-700 font-bold text-base">Loading detailed settlement records…</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-slate-200 shadow-xs p-6">
            <FaExclamationTriangle className="text-red-500 text-4xl mb-3" />
            <div className="text-red-700 font-bold text-base text-center mb-4">{error}</div>
            <button onClick={loadData} className="px-5 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-black transition">
              Retry Loading
            </button>
          </div>
        )}

        {/* CONTENT RECORDS LIST */}
        {!loading && !error && (
          <>
            {/* IF AN ENTITY IS SELECTED -> RENDER DEDICATED FULL-PAGE DETAILS SCREEN */}
            {selectedGroup ? (
              <div className="space-y-6">
                  {/* 1. TOP PROFILE & NAVIGATION BANNER */}
                  <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-950 text-white p-6 rounded-3xl shadow-lg border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => navigate("/admin/Settlementrecord")}
                        className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white font-extrabold text-xs sm:text-sm rounded-2xl backdrop-blur-md transition flex items-center gap-2 border border-white/10 shadow-xs"
                      >
                        <FaArrowLeft /> Back to All Records
                      </button>
                      <div className="h-8 w-px bg-white/10 hidden sm:block" />
                      <div className="flex items-center gap-3.5">
                        <div className="w-14 h-14 rounded-2xl bg-blue-600/30 border border-blue-400/30 flex items-center justify-center text-3xl text-blue-300 shrink-0 shadow-inner">
                          {TYPE_ICON[selectedGroup.userType] || <FaUser />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2.5 flex-wrap">
                            <h2 className="font-black text-2xl sm:text-3xl tracking-tight text-white capitalize">
                              {selectedGroup.userName}
                            </h2>
                            <span className="text-xs font-black text-blue-300 bg-blue-500/20 border border-blue-400/30 px-3 py-1 rounded-xl uppercase tracking-wider">
                              {USER_TYPE_LABELS[selectedGroup.userType] || selectedGroup.userType}
                            </span>
                            <span className="text-xs font-bold text-slate-300 bg-white/10 px-3 py-1 rounded-xl border border-white/10">
                              {selectedGroup.invoiceCount} Trip{selectedGroup.invoiceCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <p className="text-xs text-slate-300 font-medium mt-1">
                            Complete breakdown of trips, hired companies, cargo items, deductions & payout history
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <button
                        type="button"
                        disabled={selectedGroup.totalRemaining === 0}
                        onClick={() => openEntityPay(selectedGroup.key, selectedGroup.userName, selectedGroup.userType, selectedGroup.totalRemaining, true)}
                        className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-black text-xs sm:text-sm rounded-2xl transition flex items-center gap-2 shadow-md"
                        title="Settle total remaining dues across all trips"
                      >
                        <FaMoneyCheckAlt className="text-base" /> Full Pay Total ({fmt(selectedGroup.totalRemaining)})
                      </button>
                      <button
                        type="button"
                        disabled={selectedGroup.totalRemaining === 0}
                        onClick={() => openEntityPay(selectedGroup.key, selectedGroup.userName, selectedGroup.userType, selectedGroup.totalRemaining, false)}
                        className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-black text-xs sm:text-sm rounded-2xl transition flex items-center gap-2 shadow-md"
                      >
                        Partial Pay Total
                      </button>
                      <button
                        type="button"
                        onClick={() => exportToExcel([selectedGroup])}
                        className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white font-extrabold text-xs sm:text-sm rounded-2xl border border-white/10 transition flex items-center gap-2 shadow-xs"
                      >
                        <FaFileExcel className="text-base text-emerald-400" /> Export CSV
                      </button>
                    </div>
                  </div>

                  {/* 2. EXECUTIVE FINANCIAL HIGHLIGHT CARDS (5 METRICS) */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3.5 text-xs sm:text-sm">
                    {/* Gross Allocated */}
                    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-xs transition">
                      <div className="flex items-center justify-between text-slate-400 mb-1">
                        <span className="text-[11px] font-extrabold uppercase text-slate-500 tracking-wider">Gross Allocated</span>
                        <FaMoneyBillWave className="text-blue-500 text-base" />
                      </div>
                      <div className="font-black text-slate-900 text-lg sm:text-2xl mt-0.5">{fmt(selectedGroup.totalGross)}</div>
                      <span className="text-[10px] text-slate-400 font-semibold block mt-1">Before deductions</span>
                    </div>

                    {/* Total Deductions */}
                    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-xs transition">
                      <div className="flex items-center justify-between text-slate-400 mb-1">
                        <span className="text-[11px] font-extrabold uppercase text-red-600 tracking-wider">Total Deductions</span>
                        <FaTimesCircle className="text-red-500 text-base" />
                      </div>
                      <div className="font-black text-red-600 text-lg sm:text-2xl mt-0.5">
                        -{fmt(selectedGroupMetrics.totalDeductions)}
                      </div>
                      <span className="text-[10px] text-slate-400 font-semibold block mt-1">Comm, TDS, Coll, Other</span>
                    </div>

                    {/* Net Payable */}
                    <div className="bg-indigo-900 text-white p-4 rounded-2xl shadow-md hover:shadow-lg transition">
                      <div className="flex items-center justify-between text-indigo-200 mb-1">
                        <span className="text-[11px] font-extrabold uppercase tracking-wider">Net Payable</span>
                        <FaFileInvoiceDollar className="text-indigo-300 text-base" />
                      </div>
                      <div className="font-black text-white text-lg sm:text-2xl mt-0.5">{fmt(selectedGroup.totalNetPayable)}</div>
                      <span className="text-[10px] text-indigo-200 font-semibold block mt-1">Final amount due</span>
                    </div>

                    {/* Total Paid */}
                    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-xs transition">
                      <div className="flex items-center justify-between text-slate-400 mb-1">
                        <span className="text-[11px] font-extrabold uppercase text-emerald-700 tracking-wider">Total Paid</span>
                        <FaCheckCircle className="text-emerald-500 text-base" />
                      </div>
                      <div className="font-black text-emerald-700 text-lg sm:text-2xl mt-0.5">{fmt(selectedGroup.totalPaid)}</div>
                      <span className="text-[10px] text-emerald-600 font-bold block mt-1">{selectedGroupMetrics.pctPaid}% completed</span>
                    </div>

                    {/* Remaining Due */}
                    <div className={`p-4 rounded-2xl border shadow-2xs hover:shadow-xs transition col-span-2 sm:col-span-1 ${
                      selectedGroup.totalRemaining > 0 ? "bg-amber-500/10 border-amber-300 text-amber-950" : "bg-emerald-50 border-emerald-200 text-emerald-950"
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[11px] font-extrabold uppercase tracking-wider ${selectedGroup.totalRemaining > 0 ? "text-amber-800" : "text-emerald-800"}`}>
                          Remaining Due
                        </span>
                        {selectedGroup.totalRemaining > 0 ? <FaExclamationTriangle className="text-amber-600 text-base" /> : <FaCheckCircle className="text-emerald-600 text-base" />}
                      </div>
                      <div className={`font-black text-lg sm:text-2xl mt-0.5 ${selectedGroup.totalRemaining > 0 ? "text-amber-900" : "text-emerald-900"}`}>
                        {fmt(selectedGroup.totalRemaining)}
                      </div>
                      <span className={`text-[10px] font-bold block mt-1 ${selectedGroup.totalRemaining > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                        {selectedGroup.totalRemaining > 0 ? "Pending Payout" : "Fully Settled 🎉"}
                      </span>
                      {selectedGroup.totalRemaining > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          <button
                            type="button"
                            onClick={() => openEntityPay(selectedGroup.key, selectedGroup.userName, selectedGroup.userType, selectedGroup.totalRemaining, true)}
                            className="px-2 py-1 bg-emerald-600 hover:bg-slate-900 text-white text-[10px] font-extrabold rounded-lg transition shadow-2xs"
                          >
                            Full Pay Total
                          </button>
                          <button
                            type="button"
                            onClick={() => openEntityPay(selectedGroup.key, selectedGroup.userName, selectedGroup.userType, selectedGroup.totalRemaining, false)}
                            className="px-2 py-1 bg-blue-600 hover:bg-slate-900 text-white text-[10px] font-extrabold rounded-lg transition shadow-2xs"
                          >
                            Partial Pay
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 3. PAYOUT PROGRESS BAR BANNER */}
                  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-3">
                      <div className="font-extrabold text-slate-800">Payout Progress:</div>
                      <div className="font-black text-slate-900 text-sm">{selectedGroupMetrics.pctPaid}% Settled</div>
                      <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase ${
                        selectedGroupMetrics.isFullyPaid ? "bg-emerald-100 text-emerald-800" : selectedGroupMetrics.pctPaid > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                      }`}>
                        {selectedGroupMetrics.isFullyPaid ? "Fully Paid" : selectedGroupMetrics.pctPaid > 0 ? "Partially Paid" : "Unpaid"}
                      </span>
                    </div>
                    <div className="flex-1 max-w-md">
                      <div className="h-3 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${selectedGroupMetrics.isFullyPaid ? "bg-emerald-500" : selectedGroupMetrics.pctPaid > 0 ? "bg-blue-600" : "bg-slate-300"}`}
                          style={{ width: `${Math.min(100, selectedGroupMetrics.pctPaid)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  {/* 4. DETAILED TRIP ALLOCATIONS CARDS LIST */}
                  <div className="space-y-5">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <h3 className="text-base font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                        <FaRoute className="text-blue-600" /> Allocated Trips & Hired Companies ({selectedGroup.allocations.length})
                      </h3>
                    </div>

                    {selectedGroup.allocations.map(({ invoice: inv, distribution: dist }, aIdx) => {
                      const net      = getNetPayable(dist);
                      const paid     = sumPaid(dist);
                      const rem      = Math.max(0, net - paid);
                      const isPaid   = paid >= net && net > 0;
                      const totalDed = (dist.commission || 0) + (dist.tds || 0) + (dist.collection || 0) + (dist.otherDeduction || 0);

                      return (
                        <div
                          key={`${inv.id}_${aIdx}`}
                          className="relative bg-white rounded-2xl border border-[#DDE1E3] shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
                        >
                          {/* status spine */}
                          <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                            isPaid ? "bg-[#0E7C74]" : paid > 0 ? "bg-[#C2790A]" : "bg-slate-300"
                          }`} />

                          <div className="pl-5 pr-4 sm:pr-6 py-5">
                            {/* MANIFEST HEADER */}
                            <div className="flex flex-wrap items-start justify-between gap-4 pb-4 mb-4 border-b border-dashed border-[#DDE1E3]">
                              <div className="flex items-baseline gap-3 min-w-0">
                                <span className="font-mono text-3xl sm:text-4xl font-black text-[#14213D] tabular-nums leading-none shrink-0">
                                  {String(aIdx + 1).padStart(2, "0")}
                                </span>
                                <div className="min-w-0">
                                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Trip Manifest</div>
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs font-semibold text-slate-600">
                                    <span className="truncate">Hired by <span className="text-[#14213D] font-black">{safeToString(inv.company)}</span></span>
                                    {inv.lrNumber && <span className="font-mono text-[#0E7C74]">LR {safeToString(inv.lrNumber)}</span>}
                                    {inv.invoiceNumber && <span className="font-mono text-slate-500">INV {safeToString(inv.invoiceNumber)}</span>}
                                    {inv.vehicle && inv.vehicle !== "—" && (
                                      <span className="font-mono text-orange-800 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-200 font-bold flex items-center gap-1">
                                        <FaTruck className="text-xs text-orange-600 shrink-0" />
                                        <span>Vehicle: {safeToString(inv.vehicle)}</span>
                                      </span>
                                    )}
                                    {inv.driver && inv.driver !== "—" && (
                                      <span className="text-slate-600">Driver: <span className="font-bold text-slate-800">{safeToString(inv.driver)}</span></span>
                                    )}
                                    {inv.date && <span className="text-slate-400">{fmtDate(inv.date)}</span>}
                                  </div>
                                </div>
                              </div>

                              <div className={`shrink-0 -rotate-6 border-2 border-dashed rounded-lg px-3 py-1.5 text-[10px] font-black uppercase tracking-widest ${
                                isPaid ? "border-[#0E7C74] text-[#0E7C74]" : paid > 0 ? "border-[#C2790A] text-[#C2790A]" : "border-slate-300 text-slate-400"
                              }`}>
                                {isPaid ? "Settled" : paid > 0 ? "Partial" : "Unpaid"}
                              </div>
                            </div>

                            {/* ROUTE TICKET */}
                            <div className="flex flex-col sm:flex-row sm:items-stretch gap-3 mb-4">
                              <div className="flex-1 flex items-center gap-3 bg-[#F5F6F2] border border-[#DDE1E3] rounded-xl px-4 py-3">
                                <div className="min-w-0">
                                  <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Origin</div>
                                  <div className="font-black text-[#14213D] text-sm truncate">{inv.origin || "—"}</div>
                                </div>
                                <div className="flex-1 flex items-center gap-1.5 px-1 text-[#E8622C]">
                                  <span className="flex-1 border-t border-dashed border-[#E8622C]/50" />
                                  <FaTruck className="text-xs shrink-0" />
                                  <span className="flex-1 border-t border-dashed border-[#E8622C]/50" />
                                </div>
                                <div className="min-w-0 text-right">
                                  <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Destination</div>
                                  <div className="font-black text-[#14213D] text-sm truncate">{inv.destination || "—"}</div>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 text-xs">
                                <div className="flex-1 min-w-[110px] sm:w-32 bg-white border border-[#DDE1E3] rounded-xl px-3 py-2">
                                  <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Vehicle No.</div>
                                  <div className="font-bold text-slate-900 truncate flex items-center gap-1 font-mono" title={safeToString(inv.vehicle)}>
                                    <FaTruck className="text-orange-500 shrink-0 text-xs" />
                                    <span>{safeToString(inv.vehicle) || "—"}</span>
                                  </div>
                                </div>
                                {inv.driver && inv.driver !== "—" && (
                                  <div className="flex-1 min-w-[110px] sm:w-32 bg-white border border-[#DDE1E3] rounded-xl px-3 py-2">
                                    <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Driver</div>
                                    <div className="font-bold text-slate-800 truncate" title={safeToString(inv.driver)}>{safeToString(inv.driver)}</div>
                                  </div>
                                )}
                                <div className="flex-1 min-w-[110px] sm:w-32 bg-white border border-[#DDE1E3] rounded-xl px-3 py-2">
                                  <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Consignor</div>
                                  <div className="font-bold text-slate-800 truncate" title={safeToString(inv.sender)}>{safeToString(inv.sender)}</div>
                                </div>
                                <div className="flex-1 min-w-[110px] sm:w-32 bg-white border border-[#DDE1E3] rounded-xl px-3 py-2">
                                  <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Consignee</div>
                                  <div className="font-bold text-slate-800 truncate" title={safeToString(inv.receiver)}>{safeToString(inv.receiver)}</div>
                                </div>
                              </div>
                            </div>

                            {/* CARGO */}
                            {inv.items && inv.items.length > 0 && (
                              <div className="mb-4 rounded-xl border border-[#DDE1E3] overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-[#14213D] text-white/90">
                                      <th className="p-2 text-left font-bold uppercase tracking-wider text-[10px]">Product</th>
                                      <th className="p-2 text-center font-bold uppercase tracking-wider text-[10px]">HSN</th>
                                      <th className="p-2 text-center font-bold uppercase tracking-wider text-[10px]">Qty</th>
                                      <th className="p-2 text-right font-bold uppercase tracking-wider text-[10px]">Rate</th>
                                      <th className="p-2 text-right font-bold uppercase tracking-wider text-[10px]">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="font-mono tabular-nums">
                                    {inv.items.map((it, iIdx) => (
                                      <tr key={it.id || iIdx} className={iIdx % 2 ? "bg-[#F5F6F2]" : "bg-white"}>
                                        <td className="p-2 font-sans font-bold text-[#14213D]">{safeToString(it.product)}</td>
                                        <td className="p-2 text-center text-slate-500">{safeToString(it.hsn) || "—"}</td>
                                        <td className="p-2 text-center text-slate-700">
                                          {it.qty ? `${it.qty}${it.uom !== "—" ? " " + it.uom : ""}` : "—"}
                                        </td>
                                        <td className="p-2 text-right text-slate-600">{it.rate ? fmt(it.rate) : "—"}</td>
                                        <td className="p-2 text-right font-bold text-[#0E7C74]">{it.amount ? fmt(it.amount) : "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* PERFORATION DIVIDER */}
                            <div className="flex items-center gap-2 my-4">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#DDE1E3] shrink-0" />
                              <div className="flex-1 border-t border-dashed border-[#DDE1E3]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-[#DDE1E3] shrink-0" />
                            </div>

                            {/* LEDGER */}
                            <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start">
                              <div className="font-mono text-xs space-y-1.5">
                                <div className="flex justify-between text-slate-600">
                                  <span className="font-sans">
                                    Gross allocated
                                    {dist.rate && dist.rate > 0 ? (
                                      <span className="text-[10px] text-indigo-700 font-bold ml-1 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
                                        @ ₹{dist.rate}{dist.weight ? ` × ${dist.weight}` : ""}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="tabular-nums font-semibold text-[#14213D]">{fmt(dist.amount)}</span>
                                </div>
                                {(dist.commission || 0) > 0 && (
                                  <div className="flex justify-between text-[#C2790A]">
                                    <span className="font-sans">– Commission</span><span className="tabular-nums">{fmt(dist.commission!)}</span>
                                  </div>
                                )}
                                {(dist.tds || 0) > 0 && (
                                  <div className="flex justify-between text-[#C2790A]">
                                    <span className="font-sans">– TDS</span><span className="tabular-nums">{fmt(dist.tds!)}</span>
                                  </div>
                                )}
                                {(dist.collection || 0) > 0 && (
                                  <div className="flex justify-between text-[#C2790A]">
                                    <span className="font-sans">– Collection</span><span className="tabular-nums">{fmt(dist.collection!)}</span>
                                  </div>
                                )}
                                {Array.isArray(dist.otherDeductions) && dist.otherDeductions.length > 0 ? (
                                  dist.otherDeductions.map((od, odi) => (
                                    <div key={odi} className="flex justify-between text-[#C2790A]">
                                      <span className="font-sans">– {od.name || `Other #${odi + 1}`}</span>
                                      <span className="tabular-nums">{fmt(od.amount)}</span>
                                    </div>
                                  ))
                                ) : (dist.otherDeduction || 0) > 0 ? (
                                  <div className="flex justify-between text-[#C2790A]">
                                    <span className="font-sans">– {dist.otherDeductionName?.trim() || "Other"}</span>
                                    <span className="tabular-nums">{fmt(dist.otherDeduction!)}</span>
                                  </div>
                                ) : null}
                                <div className="flex justify-between pt-1.5 border-t-2 border-[#14213D] font-black text-[#14213D]">
                                  <span className="font-sans">Net payable</span><span className="tabular-nums">{fmt(net)}</span>
                                </div>
                                <div className="flex justify-between text-[#0E7C74]">
                                  <span className="font-sans">Paid</span><span className="tabular-nums font-bold">{fmt(paid)}</span>
                                </div>
                                <div className={`flex justify-between font-bold ${rem > 0 ? "text-[#C2790A]" : "text-[#0E7C74]"}`}>
                                  <span className="font-sans">Remaining</span><span className="tabular-nums">{fmt(rem)}</span>
                                </div>
                              </div>

                              <div className="flex sm:flex-col gap-2 sm:w-36 shrink-0">
                                {(() => {
                                  const distIdxInInvoice = inv.userDistributions.findIndex(
                                    (d) => d.userType === dist.userType && d.userName === dist.userName
                                  );
                                  const targetIdx = distIdxInInvoice >= 0 ? distIdxInInvoice : 0;
                                  return (
                                    <>
                                      <button
                                        disabled={isPaid}
                                        onClick={() => openPay(inv.id, targetIdx, rem, "bank", getTodayLocalDate(), true)}
                                        className="flex-1 sm:flex-none px-3 py-2 rounded-lg bg-[#0E7C74] hover:bg-[#0b625b] text-white font-bold text-xs shadow-sm disabled:opacity-30 transition flex items-center justify-center gap-1.5 whitespace-nowrap"
                                      >
                                        <FaMoneyCheckAlt /> Full pay
                                      </button>
                                      <button
                                        disabled={isPaid}
                                        onClick={() => openPay(inv.id, targetIdx, 0, "bank", getTodayLocalDate())}
                                        className="flex-1 sm:flex-none px-3 py-2 rounded-lg bg-[#14213D] hover:bg-black text-white font-bold text-xs shadow-sm disabled:opacity-30 transition whitespace-nowrap"
                                      >
                                        Partial pay
                                      </button>
                                      <button
                                        onClick={() => openEditDist(inv, targetIdx, dist)}
                                        className="flex-1 sm:flex-none px-3 py-2 rounded-lg text-[#14213D] bg-white hover:bg-[#F5F6F2] border border-[#DDE1E3] transition text-xs font-bold flex items-center justify-center gap-1.5 whitespace-nowrap"
                                      >
                                        <FaEdit /> Edit
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* PAYMENT HISTORY (RECEIPT STYLE) */}
                            {dist.paymentRecords && dist.paymentRecords.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-dashed border-[#DDE1E3]">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                                  Payment history ({dist.paymentRecords.length})
                                </div>
                                <div className="space-y-1.5">
                                  {dist.paymentRecords.map((rec, pIdx) => {
                                    const distIdxInInvoice = inv.userDistributions.findIndex(
                                      (d) => d.userType === dist.userType && d.userName === dist.userName
                                    );
                                    const targetIdx = distIdxInInvoice >= 0 ? distIdxInInvoice : 0;
                                    return (
                                      <div key={pIdx} className="flex items-center gap-2 text-xs">
                                        <span className="font-mono text-slate-400 w-20 shrink-0">{fmtDate(rec.date)}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold capitalize shrink-0 ${MODE_COLORS[rec.paymentMode] || "bg-slate-100 text-slate-700"}`}>
                                          {rec.paymentMode}
                                        </span>
                                        <span className="flex-1 border-b border-dotted border-slate-300 mx-1" />
                                        <span className="font-mono font-bold text-[#0E7C74] tabular-nums">{fmt(rec.amount)}</span>
                                        <button
                                          className="ml-2 text-slate-400 hover:text-[#14213D] font-bold shrink-0"
                                          onClick={() => openPay(inv.id, targetIdx, rec.amount, rec.paymentMode, rec.date, false, true, pIdx)}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="text-slate-400 hover:text-red-600 font-bold shrink-0"
                                          onClick={() => removePayRec(inv.id, targetIdx, pIdx)}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
            ) : (
              /* ── MASTER LIST OF ALL ENTITIES ── */
              <div className="space-y-4">
              {entityGroups.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 font-medium text-sm">
                  No matching vehicle or entity settlement records found. Try adjusting filters or search.
                </div>
              ) : (
                entityGroups.map((grp) => {
                  const pctPaid = grp.totalNetPayable > 0
                    ? Math.round((grp.totalPaid / grp.totalNetPayable) * 100)
                    : 100;
                  const isFullyPaid = grp.totalRemaining === 0 && grp.totalNetPayable > 0;

                  return (
                    <div
                      key={grp.key}
                      onClick={() => navigate("/admin/Settlementrecord/" + encodeURIComponent(grp.key))}
                      className={`bg-white rounded-2xl border-2 shadow-2xs hover:shadow-md transition cursor-pointer p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
                        isFullyPaid ? "border-green-300" : "border-slate-200 hover:border-blue-400"
                      }`}
                    >
                      <div className="flex items-center gap-3.5 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-2xl shrink-0">
                          {TYPE_ICON[grp.userType] || <FaUser className="text-blue-600" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-black text-slate-900 text-base sm:text-lg truncate flex items-center gap-2">
                            <span>{grp.userName}</span>
                            <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-lg capitalize">
                              {USER_TYPE_LABELS[grp.userType] || grp.userType}
                            </span>
                            <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-lg border border-slate-200">
                              {grp.invoiceCount} Trip{grp.invoiceCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                            {grp.userType !== "vehicle" && (() => {
                              const vSet = Array.from(new Set(grp.allocations.map((a) => safeToString(a.invoice.vehicle)).filter((v) => v && v !== "—")));
                              if (vSet.length === 0) return null;
                              return (
                                <span className="bg-orange-50 text-orange-900 border border-orange-200 px-2.5 py-0.5 rounded-lg font-bold flex items-center gap-1">
                                  <FaTruck className="text-orange-600 text-[11px]" />
                                  <span>Vehicle{vSet.length > 1 ? "s" : ""}: {vSet.join(", ")}</span>
                                </span>
                              );
                            })()}
                            <span className="bg-blue-50 text-blue-900 border border-blue-200 px-2.5 py-0.5 rounded-lg font-bold">
                              Gross: {fmt(grp.totalGross)}
                            </span>
                            {(grp.totalCommission + grp.totalTds + grp.totalCollection + grp.totalOtherDeduction) > 0 && (
                              <span className="bg-red-50 text-red-700 border border-red-200 px-2.5 py-0.5 rounded-lg font-bold">
                                Deductions: -{fmt(grp.totalCommission + grp.totalTds + grp.totalCollection + grp.totalOtherDeduction)}
                              </span>
                            )}
                            <span className="bg-indigo-50 text-indigo-950 border border-indigo-200 px-2.5 py-0.5 rounded-lg font-black">
                              Net Payable: {fmt(grp.totalNetPayable)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0">
                        <div className="text-center bg-emerald-50/60 border border-emerald-200/80 px-3 py-1.5 rounded-xl">
                          <span className="text-[10px] text-emerald-700 font-bold uppercase block">Paid</span>
                          <span className="font-extrabold text-emerald-800 text-sm sm:text-base">{fmt(grp.totalPaid)}</span>
                        </div>

                        <div className={`text-center px-3 py-1.5 rounded-xl border ${grp.totalRemaining > 0 ? "bg-amber-50/60 border-amber-200/80" : "bg-emerald-50/60 border-emerald-200/80"}`}>
                          <span className={`text-[10px] font-bold uppercase block ${grp.totalRemaining > 0 ? "text-amber-700" : "text-emerald-700"}`}>Remaining</span>
                          <span className={`font-extrabold text-sm sm:text-base ${grp.totalRemaining > 0 ? "text-amber-800" : "text-emerald-800"}`}>
                            {fmt(grp.totalRemaining)}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportToExcel([grp]);
                            }}
                            className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-slate-900 text-white font-bold text-xs transition flex items-center gap-1.5 shrink-0 shadow-2xs"
                            title={`Download CSV report for ${grp.userName}`}
                          >
                            <FaFileExcel className="text-sm" />
                            <span className="hidden sm:inline">CSV Report</span>
                          </button>

                          <div className="flex-1 min-w-[80px]">
                            <div className="text-[10px] font-bold text-slate-500 mb-0.5">{pctPaid}% paid</div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden w-20 sm:w-24 border border-slate-200">
                              <div
                                className={`h-full rounded-full transition-all ${pctPaid >= 100 ? "bg-emerald-600" : pctPaid > 0 ? "bg-blue-600" : "bg-slate-300"}`}
                                style={{ width: `${Math.min(100, pctPaid)}%` }}
                              />
                            </div>
                          </div>

                          {grp.totalRemaining > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEntityPay(grp.key, grp.userName, grp.userType, grp.totalRemaining, true);
                              }}
                              className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-slate-900 text-white font-extrabold text-xs transition flex items-center gap-1.5 shrink-0 shadow-2xs"
                              title={`Settle total remaining dues of ${fmt(grp.totalRemaining)} for ${grp.userName}`}
                            >
                              <FaMoneyCheckAlt /> Settle Total ({fmt(grp.totalRemaining)})
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/admin/Settlementrecord/" + encodeURIComponent(grp.key));
                            }}
                            className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-slate-900 text-white font-bold text-xs transition flex items-center gap-1.5 shadow-2xs"
                          >
                            View Details <FaArrowRight />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      )}

        {/* ── MODALS (Payment & Edit Distribution) ── */}
        {/* ENTITY TOTAL SETTLEMENT BULK PAYMENT MODAL */}
        {entityPayModal && (() => {
          const remToPay = Math.max(0, entityPayModal.totalRemaining);
          const isFull = !!entityPayModal.full;
          const isExceeding = !isFull && Number(entityPayModal.amt) > remToPay;

          return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
              <div className="bg-white p-6 rounded-2xl border-2 border-emerald-600 shadow-2xl w-96 max-w-[95vw] space-y-4">
                <div className="text-slate-900 font-black text-lg border-b border-slate-100 pb-3">
                  {isFull ? "Full Pay Entity Total Dues" : "Partial Pay Entity Total Dues"} for{" "}
                  <span className="text-emerald-700 capitalize font-black">{entityPayModal.userName}</span>
                </div>

                <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3 text-xs space-y-1.5 text-emerald-950">
                  <div className="flex justify-between font-bold">
                    <span className="text-slate-600">Total Entity Outstanding Due:</span>
                    <span className="text-emerald-800 text-sm">{fmt(remToPay)}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 font-medium pt-1 border-t border-emerald-200/80">
                    Payment will automatically settle pending trip dues sequentially across all trips for this entity.
                  </p>
                </div>

                {isExceeding && (
                  <div className="text-xs text-red-600 font-bold bg-red-50 p-2 rounded-xl border border-red-200">
                    ⚠️ Payment cannot exceed total remaining balance ({fmt(remToPay)})
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold mb-1 text-slate-800">
                    Payment Amount (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={remToPay}
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 text-xs w-full font-black focus:border-emerald-500 focus:outline-none"
                    value={isFull ? remToPay.toString() : entityPayModal.amt}
                    readOnly={isFull}
                    onChange={(e) => setEntityPayModal((p) => (p ? { ...p, amt: e.target.value.replace(/^0+/, "") } : p))}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold mb-1 text-slate-800">Payment Mode</label>
                  <select
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 text-xs w-full focus:border-emerald-500 focus:outline-none font-semibold"
                    value={entityPayModal.mode}
                    onChange={(e) => setEntityPayModal((p) => (p ? { ...p, mode: e.target.value as PaymentMode } : p))}
                  >
                    <option value="bank">Bank Transfer</option>
                    <option value="online">Online Payment</option>
                    <option value="cash">Cash</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold mb-1 text-slate-800">Payment Date</label>
                  <input
                    type="date"
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 text-xs w-full focus:border-emerald-500 focus:outline-none font-semibold"
                    value={formatYMD(entityPayModal.date)}
                    onChange={(e) => setEntityPayModal((p) => (p ? { ...p, date: e.target.value } : p))}
                  />
                </div>

                <div className="flex gap-2 justify-end pt-3 border-t border-slate-100">
                  <button
                    className="bg-emerald-600 rounded-xl text-white text-xs px-4 py-2 font-bold hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-xs"
                    disabled={(!isFull && Number(entityPayModal.amt) <= 0) || isExceeding}
                    onClick={() => {
                      const amt = isFull ? remToPay : Number(entityPayModal.amt);
                      handleEntityPayment(entityPayModal.grpKey, amt, entityPayModal.mode, entityPayModal.date);
                    }}
                  >
                    Confirm Entity Settlement
                  </button>
                  <button onClick={() => setEntityPayModal(null)} className="bg-slate-100 text-slate-700 px-4 py-2 rounded-xl text-xs hover:bg-slate-200 font-bold transition">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {payModal && (() => {
          const invoice = invoices.find((i) => i.id === payModal.invoiceId);
          if (!invoice) return null;
          const u = invoice.userDistributions[payModal.userIdx];
          if (!u) return null;
          const netPay = getNetPayable(u);
          const paidSoFar = u.paymentRecords.reduce((sum, r, idx) =>
            sum + (payModal.editing && payModal.editingIdx === idx ? 0 : r.amount), 0
          );
          const remToPay = Math.max(0, netPay - paidSoFar);
          const isFull = !!payModal.full;
          const isExceeding = !isFull && Number(payModal.amt) > remToPay;

          return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
              <div className="bg-white p-6 rounded-2xl border-2 border-blue-600 shadow-2xl w-96 max-w-[95vw] space-y-4">
                <div className="text-slate-900 font-black text-lg border-b border-slate-100 pb-3">
                  {payModal.editing ? "Edit Payment Record" : isFull ? "Record Full Payment" : "Record Partial Payment"} for <span className="text-blue-700 capitalize">{USER_TYPE_LABELS[u.userType] ?? u.userType}</span>
                  {u.userName && <span className="text-slate-600 font-semibold text-xs block mt-0.5">({safeToString(u.userName)})</span>}
                </div>

                <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3 text-xs space-y-1.5 text-blue-900">
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-600">Net Payable:</span>
                    <span className="font-extrabold text-blue-950">₹{netPay.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-600">Already Paid:</span>
                    <span className="font-bold text-emerald-700">₹{paidSoFar.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200/80 pt-1.5 font-black">
                    <span className="text-blue-900 uppercase text-[10px]">Amount Due to Pay:</span>
                    <span className="text-amber-700 text-xs">₹{remToPay.toLocaleString("en-IN")}</span>
                  </div>
                </div>

                {isExceeding && (
                  <div className="text-xs text-red-600 font-bold bg-red-50 p-2 rounded-xl border border-red-200">
                    ⚠️ Payment cannot exceed remaining due amount (Max: ₹{remToPay.toLocaleString("en-IN")})
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold mb-1 text-slate-800">
                    Payment Amount (₹) {remToPay > 0 && <span className="text-amber-700 font-bold ml-1">(Due: ₹{remToPay.toLocaleString("en-IN")})</span>}
                  </label>
                  <input
                    type="number" min={0} max={remToPay}
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 text-xs w-full font-bold focus:border-blue-500 focus:outline-none"
                    value={isFull ? remToPay.toString() : payModal.amt}
                    readOnly={isFull}
                    onChange={(e) => setPayModal((p) => (p ? { ...p, amt: e.target.value.replace(/^0+/, "") } : p))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 text-slate-800">Payment Mode</label>
                  <select
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 text-xs w-full focus:border-blue-500 focus:outline-none font-semibold"
                    value={payModal.mode}
                    onChange={(e) => setPayModal((p) => (p ? { ...p, mode: e.target.value as PaymentMode } : p))}
                  >
                    <option value="bank">Bank Transfer</option>
                    <option value="online">Online Payment</option>
                    <option value="cash">Cash</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 text-slate-800">Payment Date</label>
                  <input
                    type="date"
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 text-xs w-full focus:border-blue-500 focus:outline-none font-semibold"
                    value={formatYMD(payModal.date)}
                    onChange={(e) => setPayModal((p) => (p ? { ...p, date: e.target.value } : p))}
                  />
                </div>
                <div className="flex gap-2 justify-end pt-3 border-t border-slate-100">
                  <button
                    className="bg-blue-600 rounded-xl text-white text-xs px-4 py-2 font-bold hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-xs"
                    disabled={(!isFull && Number(payModal.amt) <= 0) || isExceeding}
                    onClick={() => {
                      const amt = isFull ? remToPay : Number(payModal.amt);
                      handlePayment(payModal.invoiceId, payModal.userIdx, amt, payModal.mode, payModal.date, payModal.editing ? payModal.editingIdx : undefined);
                    }}
                  >
                    {payModal.editing ? "Save Payment" : "Record Payment"}
                  </button>
                  <button onClick={() => setPayModal(null)} className="bg-slate-100 text-slate-700 px-4 py-2 rounded-xl text-xs hover:bg-slate-200 font-bold transition">Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}

        {editDist && (() => {
          const invoice = invoices.find((i) => i.id === editDist.invoiceId);
          if (!invoice) return null;
          const maxAmt = editDist.editingIdx != null
            ? invoice.payment - (invoice.userDistributions.filter((_, i) => i !== editDist.editingIdx).reduce((a, d) => a + (d.amount || 0), 0))
            : invoice.payment - (invoice.userDistributions.reduce((a, d) => a + (d.amount || 0), 0));

          return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
              <div className="bg-white p-6 rounded-2xl border-2 border-blue-600 shadow-2xl w-[460px] max-w-[95vw] space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="text-slate-900 font-black text-xl">
                  {editDist.editingIdx != null ? "Edit Settlement User Payout" : "Add Settlement User Payout"}
                </div>

                <div>
                  <label className="block text-xs font-bold mb-1">User Type</label>
                  <select
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 w-full text-xs font-semibold"
                    value={editDist.userType}
                    onChange={(e) => setEditDist((p) => (p ? { ...p, userType: e.target.value } : p))}
                  >
                    <option value="vehicle">Vehicle</option>
                    <option value="driver">Driver</option>
                    <option value="sender">Sender (Consignor)</option>
                    <option value="receiver">Receiver (Consignee)</option>
                    <option value="transporter">Transporter</option>
                    <option value="broker">Broker</option>
                    <option value="company">Company</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">User Name / Vehicle No</label>
                  <input
                    type="text"
                    className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 w-full text-xs font-semibold"
                    placeholder="Enter name or vehicle number…"
                    value={safeToString(editDist.userName)}
                    onChange={(e) => setEditDist((p) => (p ? { ...p, userName: e.target.value } : p))}
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-xs font-bold text-slate-800">Allocation Amount Mode</label>
                  </div>
                  <div className="flex gap-1.5 mb-2 bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold">
                    <button
                      type="button"
                      className={`flex-1 py-1 rounded-lg transition ${
                        (editDist.allocMode || "direct") === "direct"
                          ? "bg-white text-blue-700 shadow-2xs border border-slate-200"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      onClick={() => setEditDist((p) => p ? { ...p, allocMode: "direct" } : p)}
                    >
                      Direct Amount
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-1 rounded-lg transition ${
                        editDist.allocMode === "rate_weight"
                          ? "bg-white text-blue-700 shadow-2xs border border-slate-200"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      onClick={() => {
                        setEditDist((p) => {
                          if (!p) return p;
                          const totalItemsWeight = invoice.items && invoice.items.length > 0
                            ? invoice.items.reduce((s, it) => s + Number(it.qty || 0), 0)
                            : 0;
                          const wStr = p.itemWeight || (totalItemsWeight > 0 ? totalItemsWeight.toString() : "");
                          const rStr = p.itemRate || (invoice.items && invoice.items.length > 0 && invoice.items[0]?.rate ? invoice.items[0].rate.toString() : "");
                          const w = Number(wStr || 0);
                          const r = Number(rStr || 0);
                          const calcAmt = w && r ? (w * r).toString() : p.amount;
                          return { ...p, allocMode: "rate_weight", itemWeight: wStr, itemRate: rStr, amount: calcAmt };
                        });
                      }}
                    >
                      Rate × Weight
                    </button>
                  </div>

                  {editDist.allocMode === "rate_weight" ? (
                    <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 space-y-2 mb-2">
                      <div className="text-[11px] font-extrabold text-indigo-900 flex items-center justify-between">
                        <span>Calculate Payout by Weight & Rate</span>
                        <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded font-bold">Weight × Rate</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <label className="block font-semibold text-slate-700 mb-0.5">Weight / Qty</label>
                          <input
                            type="number" min={0} step="any"
                            className="rounded-lg px-2.5 py-1.5 border border-slate-300 text-slate-900 font-bold w-full bg-white"
                            placeholder="e.g. 10"
                            value={editDist.itemWeight || ""}
                            onChange={(e) => {
                              const wVal = e.target.value;
                              setEditDist((p) => {
                                if (!p) return p;
                                const w = Number(wVal || 0);
                                const r = Number(p.itemRate || 0);
                                return { ...p, itemWeight: wVal, amount: (w * r).toString() };
                              });
                            }}
                          />
                        </div>
                        <div>
                          <label className="block font-semibold text-slate-700 mb-0.5">Rate (₹ / Unit)</label>
                          <input
                            type="number" min={0} step="any"
                            className="rounded-lg px-2.5 py-1.5 border border-slate-300 text-slate-900 font-bold w-full bg-white"
                            placeholder="e.g. 500"
                            value={editDist.itemRate || ""}
                            onChange={(e) => {
                              const rVal = e.target.value;
                              setEditDist((p) => {
                                if (!p) return p;
                                const r = Number(rVal || 0);
                                const w = Number(p.itemWeight || 0);
                                return { ...p, itemRate: rVal, amount: (w * r).toString() };
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-1 border-t border-indigo-200/80 text-xs font-bold text-indigo-950">
                        <span>Computed Gross Amount:</span>
                        <span className="text-sm text-emerald-700 font-black">
                          ₹{((Number(editDist.itemWeight || 0) * Number(editDist.itemRate || 0)) || 0).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-bold text-slate-800">Gross Allocated Amount (₹)</label>
                      </div>
                      <input
                        type="number" min={1}
                        className="rounded-xl px-3 py-2 border border-slate-300 text-slate-900 w-full text-xs font-bold focus:border-blue-500 focus:outline-none"
                        placeholder="Enter gross amount…"
                        value={editDist.amount}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                        onChange={(e) => setEditDist((p) => (p ? { ...p, amount: e.target.value.replace(/^0+/, "") } : p))}
                      />
                    </div>
                  )}
                </div>

                <div className="border border-blue-200 rounded-xl bg-blue-50/50 p-3 space-y-3">
                  <div className="text-xs font-bold text-blue-900 uppercase tracking-wide">Deductions Breakdown (Optional)</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">1. Commission (₹)</label>
                      <input
                        type="number" min={0}
                        className="rounded-lg px-2 py-1.5 border border-slate-300 text-slate-900 text-xs w-full font-semibold"
                        placeholder="0"
                        value={editDist.commission}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                        onChange={(e) => setEditDist((p) => (p ? { ...p, commission: e.target.value.replace(/^0+/, "") } : p))}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">2. TDS (₹)</label>
                      <input
                        type="number" min={0}
                        className="rounded-lg px-2 py-1.5 border border-slate-300 text-slate-900 text-xs w-full font-semibold"
                        placeholder="0"
                        value={editDist.tds}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                        onChange={(e) => setEditDist((p) => (p ? { ...p, tds: e.target.value.replace(/^0+/, "") } : p))}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">3. Collection (₹)</label>
                      <input
                        type="number" min={0}
                        className="rounded-lg px-2 py-1.5 border border-slate-300 text-slate-900 text-xs w-full font-semibold"
                        placeholder="0"
                        value={editDist.collection}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                        onChange={(e) => setEditDist((p) => (p ? { ...p, collection: e.target.value.replace(/^0+/, "") } : p))}
                      />
                    </div>
                  </div>

                  {/* 4. DYNAMIC MULTIPLE OTHER DEDUCTIONS */}
                  <div className="pt-2 border-t border-blue-200/60 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-[11px] font-bold text-slate-800">
                        4. Other Deductions (Dynamic / Multiple)
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setEditDist((p) => {
                            if (!p) return p;
                            const newId = String(Date.now());
                            return {
                              ...p,
                              otherDeductions: [
                                ...(p.otherDeductions || []),
                                { id: newId, name: "", amount: "" },
                              ],
                            };
                          });
                        }}
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-blue-600 text-white font-bold hover:bg-slate-900 transition flex items-center gap-1 shadow-2xs"
                      >
                        + Add Deduction
                      </button>
                    </div>

                    {(!editDist.otherDeductions || editDist.otherDeductions.length === 0) ? (
                      <div className="text-[11px] text-slate-500 bg-white/80 rounded-lg p-2.5 border border-dashed border-slate-300 flex items-center justify-between">
                        <span>No extra deductions added.</span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditDist((p) => {
                              if (!p) return p;
                              return {
                                ...p,
                                otherDeductions: [{ id: String(Date.now()), name: "", amount: "" }],
                              };
                            });
                          }}
                          className="text-blue-700 font-bold hover:underline"
                        >
                          + Add Deduction
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-1.5 max-h-44 overflow-y-auto pr-0.5">
                        {editDist.otherDeductions.map((od, odIdx) => (
                          <div key={od.id || odIdx} className="flex items-center gap-1.5 bg-white p-1.5 rounded-xl border border-slate-200 shadow-2xs">
                            <input
                              type="text"
                              className="rounded-lg px-2.5 py-1.5 border border-slate-300 text-slate-900 text-xs flex-1 font-semibold focus:border-blue-500 focus:outline-none"
                              placeholder="Name (e.g. Penalty, Fuel Advance, Shortage)"
                              value={od.name}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditDist((p) => {
                                  if (!p) return p;
                                  const updated = [...(p.otherDeductions || [])];
                                  updated[odIdx] = { ...updated[odIdx], name: val };
                                  return { ...p, otherDeductions: updated };
                                });
                              }}
                            />
                            <div className="relative w-28">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">₹</span>
                              <input
                                type="number"
                                min={0}
                                className="rounded-lg pl-5 pr-2 py-1.5 border border-slate-300 text-slate-900 text-xs w-full font-bold focus:border-blue-500 focus:outline-none"
                                placeholder="Amount"
                                value={od.amount}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/^0+/, "");
                                  setEditDist((p) => {
                                    if (!p) return p;
                                    const updated = [...(p.otherDeductions || [])];
                                    updated[odIdx] = { ...updated[odIdx], amount: val };
                                    return { ...p, otherDeductions: updated };
                                  });
                                }}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setEditDist((p) => {
                                  if (!p) return p;
                                  const updated = (p.otherDeductions || []).filter((_, i) => i !== odIdx);
                                  return { ...p, otherDeductions: updated };
                                });
                              }}
                              className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                              title="Remove deduction"
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {(() => {
                  const gross = Number(editDist.amount || 0);
                  const comm = Number(editDist.commission || 0);
                  const tds = Number(editDist.tds || 0);
                  const coll = Number(editDist.collection || 0);
                  const othTotal = (editDist.otherDeductions || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
                  const totalDed = comm + tds + coll + othTotal;
                  const netPayable = Math.max(0, gross - totalDed);

                  return (
                    <div className="space-y-2">
                      <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-xl p-3.5 text-xs space-y-1.5">
                        <div className="flex justify-between text-slate-300">
                          <span>Gross Amount:</span>
                          <span className="font-semibold">₹{gross.toLocaleString("en-IN")}</span>
                        </div>
                        {totalDed > 0 && (
                          <div className="text-[11px] space-y-0.5 border-t border-slate-700 pt-1 text-red-200">
                            {comm > 0 && <div className="flex justify-between"><span>• Commission:</span><span>- ₹{comm.toLocaleString("en-IN")}</span></div>}
                            {tds > 0 && <div className="flex justify-between"><span>• TDS:</span><span>- ₹{tds.toLocaleString("en-IN")}</span></div>}
                            {coll > 0 && <div className="flex justify-between"><span>• Collection:</span><span>- ₹{coll.toLocaleString("en-IN")}</span></div>}
                            {(editDist.otherDeductions || []).map((od, i) => {
                              const a = Number(od.amount || 0);
                              if (a <= 0) return null;
                              return (
                                <div key={i} className="flex justify-between">
                                  <span>• {od.name.trim() || `Other #${i + 1}`}:</span>
                                  <span>- ₹{a.toLocaleString("en-IN")}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="flex justify-between font-extrabold text-xs border-t border-slate-700 pt-1.5 text-emerald-300">
                          <span>Net Payable Amount:</span>
                          <span className="text-sm">₹{netPayable.toLocaleString("en-IN")}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                  {(() => {
                    const gross = Number(editDist.amount || 0);
                    const comm = Number(editDist.commission || 0);
                    const tds = Number(editDist.tds || 0);
                    const coll = Number(editDist.collection || 0);
                    const othTotal = (editDist.otherDeductions || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
                    const isInvalid = !editDist.userType || gross < 1 || (comm + tds + coll + othTotal) > gross;
                    return (
                      <button
                        className="bg-blue-600 rounded-xl text-white text-xs px-4 py-2 font-bold hover:bg-slate-900 border-2 border-blue-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition"
                        disabled={isInvalid}
                        onClick={saveEditDist}
                      >
                        {editDist.editingIdx != null ? "Save Changes" : "Add Payout"}
                      </button>
                    );
                  })()}
                  <button onClick={() => setEditDist(null)} className="bg-slate-100 text-slate-700 px-4 py-2 rounded-xl text-xs hover:bg-slate-200 font-bold transition">Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SettlementRecords;